import { createChimePlayer, type ChimePlayer, type ChimeTone } from './chime';
import { createChimeScheduler, type ChimeRequest, type ChimeScheduler } from './chimeScheduler';
import type { NotificationPrefs } from './notificationPrefs';
import { createTabLeader, documentTabRank, type TabLeader } from './tabLeader';
import type { StaffNotification } from '../services/notificationService';

/**
 * Wires the chime rules to the speaker: preferences, the one-tab claim, the
 * autoplay lock and the optional desktop notification. React-free so the
 * provider stays small and this stays testable.
 */

export type DesktopPermission = NotificationPermission | 'unsupported';

export interface DesktopNotifier {
  permission: () => DesktopPermission;
  request: () => Promise<DesktopPermission>;
  show: (item: StaffNotification, onClick: () => void) => void;
}

function notificationApi(): typeof Notification | null {
  return typeof window !== 'undefined' && typeof window.Notification === 'function' ? window.Notification : null;
}

export const browserDesktopNotifier: DesktopNotifier = {
  permission: () => notificationApi()?.permission ?? 'unsupported',
  request: async () => {
    const api = notificationApi();
    if (!api) return 'unsupported';
    try {
      return await api.requestPermission();
    } catch {
      return api.permission;
    }
  },
  show: (item, onClick) => {
    const api = notificationApi();
    if (!api || api.permission !== 'granted') return;
    try {
      const shown = new api(item.title, {
        body: item.preview ?? undefined,
        // One desktop entry per notification: a repeat replaces it instead of stacking.
        tag: `tg-notification-${item.id}`,
      });
      shown.onclick = () => {
        onClick();
        shown.close();
      };
    } catch {
      // Some platforms (Android Chrome) only allow notifications from a service worker.
    }
  },
};

/** An unlocked tab can actually make a sound — it beats a locked one, whatever is in front. */
const UNLOCKED_RANK_BONUS = 3;

export interface NotificationSoundOptions {
  getPrefs: () => NotificationPrefs;
  /** true = a chime was due but the autoplay lock swallowed it („Ton aktivieren“). */
  onBlockedChange: (blocked: boolean) => void;
  onDesktopClick: (item: StaffNotification) => void;
  now: () => number;
  player?: ChimePlayer;
  leader?: TabLeader;
  desktop?: DesktopNotifier;
}

export interface NotificationSound {
  scheduler: ChimeScheduler;
  tabId: string;
  /** From a click: unlock audio and play the tone here (no tab claim — the person asked for it). */
  preview: (tone: ChimeTone) => void;
  /** From a click: unlock audio. Resolves true when sound can play. */
  enable: () => Promise<boolean>;
  destroy: () => void;
}

export function createNotificationSound(options: NotificationSoundOptions): NotificationSound {
  const player = options.player ?? createChimePlayer();
  const desktop = options.desktop ?? browserDesktopNotifier;
  const leader =
    options.leader ??
    createTabLeader({ rank: () => documentTabRank() + (player.isUnlocked() ? UNLOCKED_RANK_BONUS : 0) });
  let removeGestureUnlock: (() => void) | null = null;

  const armGestureUnlock = () => {
    if (removeGestureUnlock) return;
    removeGestureUnlock = player.installGestureUnlock(() => {
      removeGestureUnlock = null;
      options.onBlockedChange(false);
    });
  };

  const onChime = (request: ChimeRequest) => {
    const prefs = options.getPrefs();
    const wantsSound = prefs.soundEnabled && (request.tone === 'urgent' || prefs.softSoundsEnabled);
    const item = request.notification;
    const wantsDesktop =
      request.first &&
      request.tone === 'urgent' &&
      item !== null &&
      prefs.desktopEnabled &&
      desktop.permission() === 'granted';
    if (!wantsSound && !wantsDesktop) return;

    leader.runOnce(
      request.key,
      () => {
        if (wantsSound) {
          void player.play(request.tone, options.getPrefs().volume).then((result) => {
            if (result === 'played') {
              options.onBlockedChange(false);
              return;
            }
            if (result !== 'blocked') return;
            options.onBlockedChange(true);
            // Safari can suspend an unlocked context again; the next click must unlock it anew.
            armGestureUnlock();
          });
        }
        if (wantsDesktop && item && typeof document !== 'undefined' && document.hidden) {
          desktop.show(item, () => options.onDesktopClick(item));
        }
      },
      request.throttle,
    );
  };

  const scheduler = createChimeScheduler({ onChime, now: options.now });
  armGestureUnlock();

  const enable = async () => {
    const ok = await player.unlock();
    if (ok) options.onBlockedChange(false);
    return ok;
  };

  return {
    scheduler,
    tabId: leader.tabId,
    preview: (tone) => {
      void enable().then((ok) => {
        if (ok) void player.play(tone, options.getPrefs().volume);
      });
    },
    enable,
    destroy: () => {
      scheduler.destroy();
      leader.destroy();
      removeGestureUnlock?.();
      removeGestureUnlock = null;
      player.close();
    },
  };
}
